import { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import apiManager from '../../api/apiManager';
import API_ENDPOINTS from '../../api/apiEndpoints';
import { showErrorMessage, showSuccessMessage } from "../../shared/Services/toasterService";
import { showLoader, hideLoader } from "../../shared/Services/Loader/loaderService";
import FormLabel from "../../shared/helpers/FormLabel";
import { useLocalization } from '../../context/LocalizationContext';
import { useVehicleContext } from '../../context/VehicleContext';

const VehicleFormModal = ({
  isOpen,
  onClose,
  onSave,
  initialValues = {
    year: "",
    make: "",
    model: "",
    submodel: "",
    vin: ""
  }
}) => {
  const ctx = useVehicleContext();
  const { customerId } = ctx;
  const [years, setYears] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [submodels, setSubModels] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMake, setSelectedMake] = useState("");
  const [selectedMakeId, setSelectedMakeId] = useState("");
  const [selectedModelId, setSelectedModelId] = useState("");
  const [selectedSubModelId, setSelectedSubModelId] = useState("");
  const [vehicleEditData, setVehicleEditData] = useState({});
  const [isVinMode, setIsVinMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useLocalization();

  useEffect(() => {
    if (!isOpen) return;
    const fetchYears = async () => {
      try {
        showLoader();
        const res = await apiManager.get(API_ENDPOINTS.API_CUSTOMER_GET_YEAR_MOTOR);
        if (res.status === 200 && Array.isArray(res.data)) {
          setYears(res.data);
        }
      } catch (err) {
        showErrorMessage(err);
      } finally {
        hideLoader();
      }
    };
    fetchYears();
  }, [isOpen]);

  const engineBodyData = async (year, makeId, modelId, subModelId) => {
    const payload = {
      year: year ? Number(year) : null,
      makeId: makeId ? Number(makeId) : null,
      modelId: modelId ? Number(modelId) : null,
      submodelId: subModelId ? Number(subModelId) : null,
    };
    try {
      showLoader();
      const response = await apiManager.post(API_ENDPOINTS.CUSTOMER_GET_ENGINE_BODY, payload);
      if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
        return response.data[0];
      }
      return {};
    } catch (err) {
      showErrorMessage(err);
      return {};
    } finally {
      hideLoader();
    }
  };

  const fetchVinDetails = async (vin, setFieldValue) => {
    if (!vin || vin.length < 3) return;
    try {
      const payload = { vin };
      const response = await apiManager.post(API_ENDPOINTS.CUSTOMER_GET_VEHICLE_BY_VIN, payload);
      if (response.status === 200) {
        const data = response.data;
        if (data) {
          const { year, makeID, makeName, modelName, modelID, subModelID, subModelName } = data;
          setIsVinMode(true);
          setYears([{ year }]);
          setSelectedYear(year);
          setFieldValue('year', year);
          setMakes([{ makeID, make: makeName }]);
          setSelectedMake(makeName);
          setSelectedMakeId(makeID?.toString?.() || makeID);
          setFieldValue('make', makeName);
          setModels([{ modelID, model: modelName }]);
          setSelectedModelId(modelID?.toString?.() || modelID);
          setFieldValue('model', modelName);
          setSubModels([{ subModelID: Number(subModelID), subModelName }]);
          setSelectedSubModelId(subModelID?.toString?.() || subModelID);
          setFieldValue('submodel', Number(subModelID));
          setVehicleEditData(prev => ({ ...prev, subModelName: subModelName || '' }));
          const engineBody = await engineBodyData(year, makeID, Number(modelID), Number(subModelID));
          setVehicleEditData(prev => ({ ...prev, engine: engineBody.engine || engineBody.Engine || '', body: engineBody.body || engineBody.Body || '' }));
        }
      }
    } catch (err) {
      showErrorMessage(err);
    }
  };

  const handleYearChange = async (year, setFieldValue) => {
    setSelectedYear(year);
    setFieldValue('year', year);
    setMakes([]);
    setModels([]);
    setSubModels([]);
    if (!year || isVinMode) return;
    try {
      const response = await apiManager.post(API_ENDPOINTS.CUSTOMER_GET_YEAR_MAKES, { year });
      if (response.status === 200 && Array.isArray(response.data)) {
        setMakes(response.data.map(item => ({ ...item, makeID: item.makeID?.toString?.() || item.makeID })));
      }
    } catch (err) {
      showErrorMessage(err);
    }
  };

  const handleMakeChange = async (makeName, year, setFieldValue) => {
    setFieldValue('make', makeName);
    setSelectedMake(makeName);
    setModels([]);
    setSubModels([]);
    if (!makeName || !year || isVinMode) return;
    const selectedMake = makes.find(m => m.make === makeName || m.makeName === makeName);
    if (selectedMake) setSelectedMakeId(selectedMake.makeID?.toString?.() || selectedMake.makeID);
    if (!selectedMake) return;
    try {
      const response = await apiManager.post(API_ENDPOINTS.CUSTOMER_GET_YEAR_MOTOR, { year, makeId: selectedMake.makeID });
      if (response.status === 200) setModels(response.data || []);
    } catch (err) {
      showErrorMessage(err);
    }
  };

  const handleModelChange = async (modelName, year, makeName, setFieldValue) => {
    setFieldValue('model', modelName);
    setSubModels([]);
    if (!modelName || !year || !makeName || isVinMode) return;
    const selectedMake = makes.find(m => m.make === makeName || m.makeName === makeName);
    const selectedModel = models.find(m => m.model === modelName || m.modelName === modelName);
    if (selectedModel) setSelectedModelId(selectedModel.modelID?.toString?.() || selectedModel.modelID);
    if (!selectedMake || !selectedModel) return;
    try {
      const response = await apiManager.post(API_ENDPOINTS.CUSTOMER_GET_SUBMODELS_MOTOR, {
        year,
        makeId: selectedMake.makeID,
        modelId: selectedModel.modelID
      });
      if (response.status === 200) setSubModels(response.data || []);
    } catch (err) {
      showErrorMessage(err);
    }
  };

  const addVehicle = async (vehiclePayload) => {
    try {
      showLoader();
      const res = await apiManager.post(API_ENDPOINTS.CUSTOMER_CREATE_VEHICLE, vehiclePayload);
      showSuccessMessage(t('vehicle_added_successfully'));
      return res.data;
    } catch (err) {
      showErrorMessage(err);
    } finally {
      hideLoader();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal show d-block add-new-vehicle-modal" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t("add_vehicle")}</h5>
          </div>
          <div className="modal-body">
            <Formik
              initialValues={initialValues}
              enableReinitialize={true}
              validationSchema={Yup.object().shape({
                year: Yup.string().required(t("yup_year_required")).matches(/^\d{4}$/, t("yup_year_digits")),
                make: Yup.string().required(t("yup_make_required")),
                model: Yup.string().required(t("yup_model_required")),
                vin: Yup.string().min(17, t("yup_vin_len")).max(17, t("yup_vin_len")),
              })}
              onSubmit={async (values, { setSubmitting }) => {
                setLoading(true);
                try {
                  const selectedMake = makes.find(m => m.make === values.make || m.makeName === values.make);
                  const selectedModel = models.find(m => m.model === values.model || m.modelName === values.model);
                  const selectedSubModelId = values.submodel;
                  const year = values.year ? Number(values.year) : null;
                  if (!selectedMake || !selectedModel || !selectedSubModelId || !year) {
                    showErrorMessage(t('error_select_year_make_model_submodel'));
                    setSubmitting(false);
                    return;
                  }
                  let engine = vehicleEditData?.engine || '';
                  let body = vehicleEditData?.body || '';
                  if (!engine || !body) {
                    const eb = await engineBodyData(year, selectedMake.makeID, selectedModel.modelID, selectedSubModelId);
                    engine = eb.engine || eb.Engine || '';
                    body = eb.body || eb.Body || '';
                  }
                  const payload = {
                    year,
                    customerId,
                    makeName: values.make,
                    modelName: values.model,
                    makeId: selectedMake.makeID,
                    modelId: selectedModel.modelID,
                    subModelId: selectedSubModelId,
                    subModelName: (Array.isArray(submodels) ? (submodels.find(sm => String(sm.subModelID) === String(selectedSubModelId))?.subModelName || '') : ''),
                    vinLast6Digit: values.vin?.slice(-6) || null,
                    vin: values.vin || null,
                    engine,
                    body
                  };
                  const result = await addVehicle(payload);
                  if (onSave) onSave(result?.data);
                  onClose();
                } catch (err) {
                  showErrorMessage(err);
                } finally {
                  setLoading(false);
                  setSubmitting(false);
                }
              }}
            >
              {({ values, setFieldValue, dirty, isValid, isSubmitting }) => (
                <Form>
                  <div className="card-body">
                    <div className="container-fluid">
                      <div className="row flex-column flex-md-row">
                        <div className="col-md-5 left-panel">
                          {/* year */}
                          <div className="form-group mb-3">
                            <FormLabel htmlFor="year" label={t("year")} required={true} className="form-label" />
                            <Field as="select" name="year" className="form-control form-select" onChange={e => { setFieldValue('year', e.target.value); handleYearChange(e.target.value, setFieldValue); }}>
                              <option value="">{t("select_year")}</option>
                              {Array.isArray(years) && years.map((item) => (
                                <option key={item.year || item} value={item.year || item}>{item.year || item}</option>
                              ))}
                            </Field>
                            <div className="text-danger small">
                              <ErrorMessage name="year" />
                            </div>
                          </div>

                          {/* make */}

                          <div className="form-group mb-3 mt-2">
                            <FormLabel htmlFor="make" label={t("make")} required={true} className="form-label" />
                            <Field as="select" name="make" className="form-control form-select" onChange={e => { setFieldValue('make', e.target.value); handleMakeChange(e.target.value, values.year, setFieldValue); }}>
                              <option value="">{t('select_make')}</option>
                              {Array.isArray(makes) && makes.map((item) => (
                                <option key={item.makeID || item.make} value={item.make || item.makeName}>{item.make || item.makeName}</option>
                              ))}
                            </Field>
                            <div className="text-danger small">
                              <ErrorMessage name="make" />
                            </div>
                          </div>


                          {/* Select Model */}
                          <div className="form-group mb-3 mt-2">
                            <FormLabel htmlFor="model" label={t("model")} required={true} className="form-label" />
                            <Field as="select" name="model" className="form-control form-select" onChange={e => { setFieldValue('model', e.target.value); handleModelChange(e.target.value, values.year, values.make, setFieldValue); }}>
                              <option value="">{t('select_model')}</option>
                              {Array.isArray(models) && models.map((item) => (
                                <option key={item.modelID || item.model} value={item.model || item.modelName}>{item.model || item.modelName}</option>
                              ))}
                            </Field>
                            <div className="text-danger small">
                              <ErrorMessage name="model" />
                            </div>

                          </div>

                          {/* sub model */}
                          <div className="form-group mb-3">
                            <FormLabel htmlFor="submodel" label={t("sub_model")} required={true} className="form-label" />
                            <Field as="select" name="submodel" className="form-control form-select" onChange={e => { setFieldValue('submodel', e.target.value); setSelectedSubModelId(e.target.value); }}>
                              <option value="">{t('select_submodel')}</option>
                              {Array.isArray(submodels) && submodels.map((item) => (
                                <option key={item.subModelID || item.submodel} value={item.subModelID || item.submodel}>{item.subModelName || item.subModel || item.submodel}</option>
                              ))}
                            </Field>
                            <div className="text-danger small">
                              <ErrorMessage name="submodel" />
                            </div>

                          </div>
                        </div>
                        <div className="col-md-2 d-flex justify-content-center align-items-center middle-panel">
                          <div className="or-text">
                            <span>{t('or')}</span>
                          </div>
                        </div>
                        <div className="col-md-5 d-flex align-items-center right-panel">
                          {/* VIN */}
                          <div className="form-group w-100">
                            <FormLabel htmlFor="vin" label={t("vin")} required={false} className="form-label text-end" />
                            <div className="input-group">
                              <Field name="vin" type="text" className="form-control" placeholder={t('vin')} />
                              <button className="btn btn-success" type="button" onClick={async () => await fetchVinDetails(values.vin, setFieldValue)}>
                                <i className="fas fa-search"></i>
                              </button>
                            </div>
                            <div className="text-danger small">
                              <ErrorMessage name="vin" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-center card-footer gap-3">                  
                    <button type="submit" className="btn btn-success" disabled={!dirty || isSubmitting}>
                      {t("save")}
                    </button>
                    <button type="button" className="btn btn-cancel" onClick={onClose}>
                      {t("cancel")}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleFormModal;